import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import GmModal from 'app/shared/modal/modal';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { truncateText } from 'app/shared/utils/general';
import { useOrganizationService } from 'hooks/organization';
import { useTenantService } from 'hooks/iam/tenant';
import { useMembershipService } from 'hooks/iam/membership';
import { useCreateOrganization } from 'contexts/organization';
import { urls } from 'services/_url';
import { RaiseInvoiceModal } from './components/invoice';
import { BuySubscriptionModal } from './components/subscription';
import useScreenSize from 'hooks/size';

const Organizations = () => {
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const agency = useSelector((state) => state.metadata.agency);
  const { email, id: user_id } = useSelector((state) => state.user_data);
  const { fetchOrganizations } = useOrganizationService();
  const { unlinkAgent } = useTenantService();
  const { generateAccountCredentials } = useMembershipService();
  const { openCreateOrganization } = useCreateOrganization();

  const [organizations, setOrganizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [invoice_org, setInvoiceOrg] = useState(null);
  const [subscription_org, setSubscriptionOrg] = useState(null);
  const [unlink_org, setUnlinkOrg] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [viewing_org, setViewingOrg] = useState(null);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Organisations', path: '.' }]));
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    const { orgs, size } = await fetchOrganizations({
      query_string: agency?.id ? `agent_id=${agency.id}` : ''
    });
    setLoading(false);
    setOrganizations(orgs || []);
    setTotal(size || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency]);

  const config = {
    actions: {
      single: () => [
        [
          {
            label: 'Raise invoice',
            value: 'raise_invoice',
            hidden: agency?.track !== 'freelancer'
          },
          {
            label: 'Buy subscription',
            value: 'buy_subscription',
            hidden: agency?.track !== 'business'
          }
        ],
        [
          {
            label: 'View in app',
            value: 'view_in_app',
            hidden: !!viewing_org
          },
          { color: 'red', label: 'Unlink client', value: 'unlink_client' }
        ]
      ],
      bulk: null
    },
    fields: [
      { title: 'Name', key: 'name', isTitle: true },
      {
        title: 'Website',
        key: 'website',
        formatter: (value) => truncateText(value, 30) || 'N/A',
        isTagline: true
      },
      { title: 'Industry', key: 'industry', formatter: (value) => value || 'N/A' },
      {
        title: 'Date created',
        key: 'created_on',
        formatter: (value) => (value ? new Date(value).toDateString() : ''),
        isMetadata: true
      }
    ],
    is_search_mode,
    items: organizations,
    search_key: 'name',
    search_text: 'Search organisations'
  };

  const openModal = () => openCreateOrganization({ onCreated: loadOrganizations });

  const handleDatatableAction = ({ name, type, data }) => {
    if (type !== 'single') return;
    if (name === 'view_in_app') handleViewInApp(data);
    if (name === 'raise_invoice') setInvoiceOrg(data);
    if (name === 'buy_subscription') setSubscriptionOrg(data);
    if (name === 'unlink_client') setUnlinkOrg(data);
  };

  // Mint credentials for the selected org and hand the agent off to the main app.
  const handleViewInApp = async (org) => {
    if (viewing_org) return;

    setViewingOrg(org);
    const credentials = await generateAccountCredentials({
      data: { email, user_id, role_id: 1, tenant_id: org.id }
    });

    if (!credentials) {
      setViewingOrg(null);
      return;
    }

    const { token, tenant_id, id } = credentials;
    window.location.href = `${urls.app}?t=${token}&tenant_id=${tenant_id}&user_id=${id}`;
  };

  // Unlink the client: drop the agent from the tenant, then from the list.
  const handleUnlink = async (proceed) => {
    if (!proceed) return setUnlinkOrg(null);

    setUnlinking(true);
    const unlinked = await unlinkAgent(unlink_org.id, {
      data: { agent_id: agency?.id, tenant_id: unlink_org.id }
    });
    setUnlinking(false);

    if (!unlinked) return;
    setOrganizations((prev) => prev.filter((org) => org.id !== unlink_org.id));
    setTotal((prev) => Math.max(0, prev - 1));
    setUnlinkOrg(null);
  };

  const table_actions = <Button text="Create organisation" icon_name="add" onClick={openModal} />;

  return (
    <PageContentWrapper>
      {isMobile ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onListModeChange={setSearchMode}
          onDataRequest={loadOrganizations}
          showHeader
        />
      ) : (
        <WebDatatable
          config={{ ...config, total_count: total }}
          action={handleDatatableAction}
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={loadOrganizations}
        />
      )}

      <GmModal
        title="Raise invoice"
        show_title
        show_modal={!!invoice_org}
        onClose={() => setInvoiceOrg(null)}
      >
        <RaiseInvoiceModal organization={invoice_org || {}} />
      </GmModal>

      <GmModal
        title="Buy subscription"
        show_title
        show_modal={!!subscription_org}
        onClose={() => setSubscriptionOrg(null)}
      >
        <BuySubscriptionModal organization={subscription_org || {}} />
      </GmModal>

      <ConfirmationDialog
        is_open={!!unlink_org}
        is_loading={unlinking}
        type="error"
        icon_name="cancel"
        title="Unlink client"
        action_text="Unlink"
        message={
          <span>
            Are you sure you want to unlink <strong>{unlink_org?.name}</strong>? They will no longer
            appear in your organisations.
          </span>
        }
        callback={handleUnlink}
      />
    </PageContentWrapper>
  );
};

export default Organizations;
